import { FC } from 'react';
import { UseObjectFormObject, useObjectForm } from '@app/hooks';
import {
  ValueOrVariableAutocomplete,
  VariableAutocomplete
} from '@app/page-components/shared';
import { TrustedResponseObjectConfig, VariableType } from '@app/types';
import { NodeFooter } from './NodeFooter';
import { NodeHeader } from './NodeHeader';

export type TrustedResponseNodeModelFormProps = {
  object: UseObjectFormObject<TrustedResponseObjectConfig>;
};

export const TrustedResponseNodeModelForm: FC<TrustedResponseNodeModelFormProps> = ({
  object
}) => {
  const { createSubmitHandler, getControlProps } = useObjectForm({ object });

  return (
    <form
      key="form"
      className="flex-column flex-gap"
      onSubmit={createSubmitHandler()}
    >
      <NodeHeader getControlProps={getControlProps} />
      <ValueOrVariableAutocomplete
        {...getControlProps('task')}
        label="Task ID or Name"
        placeholder="Task from the GAI service..."
        tooltip="The task name or UUID from the GAI configuration service."
        valueType={VariableType.string}
      />
      <ValueOrVariableAutocomplete
        {...getControlProps('userInput')}
        label="User Input"
        placeholder="Input for the generative system..."
        tooltip="The users input to be passed to the generative system."
        valueType={VariableType.string}
      />
      <VariableAutocomplete
        {...getControlProps('sessionContext')}
        label="Session Context"
        placeholder="Additional data..."
        searchTypes={[VariableType.string]}
        tooltip="Additional session context (data) for the generative system to use (API responses, session data, etc...)."
      />
      <VariableAutocomplete
        {...getControlProps('conversationHistory')}
        label="Conversation History"
        placeholder="Additional conversation history..."
        searchTypes={[VariableType.string]}
        tooltip="Additional conversation history to inform the generative system on previous turns not managed by the LLM Proxy."
      />
      <ValueOrVariableAutocomplete
        {...getControlProps('curatedDataLimit')}
        label="Curated Data Limit"
        placeholder="Max data points..."
        tooltip="The maximum number of curated data points to use for a generation (0 = no limit)."
        valueType={VariableType.number}
      />
      <ValueOrVariableAutocomplete
        {...getControlProps('useTrustedResponse')}
        label="Use Trusted Response"
        placeholder="Use curated RAG data."
        tooltip="If enabled, curated RAG data will be used instead of the raw ingested customer data."
        valueType={VariableType.boolean}
      />
      <VariableAutocomplete
        {...getControlProps('response')}
        label="RAG Response"
        placeholder="Save response in this variable..."
        searchTypes={[VariableType.string]}
        tooltip="The variable to assign the RAG response output to."
      />
      <NodeFooter getControlProps={getControlProps} />
    </form>
  );
};
